import type { AuthMeResponse } from '@zrsystem/contracts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  fetchCurrentUser,
  loginRequest,
  refreshSessionRequest,
} from '@/features/auth/api/auth.api.js';
import { createHttpClient } from '@/shared/api/http-client.js';
import { clearAccessToken, getAccessToken, setAccessToken } from '@/shared/auth/auth-storage.js';
import { setUnauthorizedHandler } from '@/shared/auth/unauthorized-handler.js';
import { getActivePlaceId } from '@/shared/workspace/active-place-bridge.js';

export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthMeResponse | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refreshUser: () => Promise<AuthMeResponse | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return value;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthMeResponse | null>(null);
  const refreshInFlight = useRef<Promise<boolean> | null>(null);

  const tryRefresh = useCallback(async (): Promise<boolean> => {
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }

    refreshInFlight.current = (async () => {
      try {
        const session = await refreshSessionRequest();
        setAccessToken(session.accessToken);
        return true;
      } catch {
        clearAccessToken();
        setUser(null);
        setStatus('unauthenticated');
        return false;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    return refreshInFlight.current;
  }, []);

  useEffect(() => {
    createHttpClient({
      getAccessToken,
      getActivePlaceId,
      onUnauthorized: tryRefresh,
    });

    setUnauthorizedHandler(tryRefresh);

    let cancelled = false;

    async function bootstrap() {
      try {
        if (!getAccessToken()) {
          const refreshed = await tryRefresh();
          if (!refreshed) {
            if (!cancelled) {
              setStatus('unauthenticated');
            }
            return;
          }
        }

        const me = await fetchCurrentUser();
        if (!cancelled) {
          setUser(me);
          setStatus('authenticated');
        }
      } catch {
        clearAccessToken();
        if (!cancelled) {
          setUser(null);
          setStatus('unauthenticated');
        }
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
      setUnauthorizedHandler(null);
    };
  }, [tryRefresh]);

  const refreshUser = useCallback(async (): Promise<AuthMeResponse | null> => {
    try {
      const me = await fetchCurrentUser();
      setUser(me);
      setStatus('authenticated');
      return me;
    } catch {
      clearAccessToken();
      setUser(null);
      setStatus('unauthenticated');
      return null;
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await loginRequest({ email, password });
    setAccessToken(session.accessToken);
    const me = await fetchCurrentUser();
    setUser(me);
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(() => {
    clearAccessToken();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      signIn,
      signOut,
      refreshUser,
    }),
    [refreshUser, signIn, signOut, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
